import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Mail, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { EmailDraftDialog } from './EmailDraftDialog';
import { useGenerateEmailDraft, useSendQuoteEmail } from '../api/hooks';
import { loadContractorSettings } from '../lib/contractorSettings';
import type { QuoteStatus } from '../api/types';

interface EmailDraftButtonProps {
  quoteId: string;
  clientEmail?: string | null;
  quoteStatus: QuoteStatus;
  hasItems: boolean;
  onStatusChange?: (status: QuoteStatus) => Promise<void>;
  getAccessToken: () => Promise<string>;
  className?: string;
}

export function EmailDraftButton({
  quoteId,
  clientEmail,
  quoteStatus,
  hasItems,
  onStatusChange,
  getAccessToken,
  className,
}: Readonly<EmailDraftButtonProps>) {
  const { t, i18n } = useTranslation();
  const [showDialog, setShowDialog] = useState(false);
  const emailDraft = useGenerateEmailDraft(getAccessToken);
  const sendEmail = useSendQuoteEmail(getAccessToken);

  const handleGenerate = useCallback(() => {
    const settings = loadContractorSettings();
    emailDraft.mutate(
      {
        id: quoteId,
        language: i18n.language.startsWith('et') ? 'et' : 'en',
        contractor: {
          companyName: settings.companyName || undefined,
          paymentTermDays: settings.paymentTermDays || undefined,
          validityDays: settings.validityDays || undefined,
        },
      },
      {
        onSuccess: () => setShowDialog(true),
        onError: () => toast.error(t('email.error')),
      },
    );
  }, [quoteId, i18n.language, emailDraft, t]);

  const handleRegenerate = useCallback(() => {
    const settings = loadContractorSettings();
    emailDraft.mutate({
      id: quoteId,
      language: i18n.language.startsWith('et') ? 'et' : 'en',
      contractor: {
        companyName: settings.companyName || undefined,
        paymentTermDays: settings.paymentTermDays || undefined,
        validityDays: settings.validityDays || undefined,
      },
    });
  }, [quoteId, i18n.language, emailDraft]);

  const handleSendEmail = useCallback(
    async (to: string) => {
      sendEmail.mutate(
        {
          quoteId,
          to,
          message: emailDraft.data?.body,
          subject: emailDraft.data?.subject,
        },
        {
          onSuccess: () => {
            toast.success(t('email.sent'));
            if (quoteStatus === 'draft' && onStatusChange) {
              void onStatusChange('sent');
            }
            setShowDialog(false);
          },
          onError: () => toast.error(t('email.sendFailed')),
        },
      );
    },
    [quoteId, emailDraft.data, sendEmail, quoteStatus, onStatusChange, t],
  );

  if (!hasItems) return null;

  return (
    <>
      <Button
        variant="outline"
        onClick={handleGenerate}
        disabled={emailDraft.isPending}
        className={className}
        data-testid="email-draft-btn"
      >
        {emailDraft.isPending ? (
          <Loader2 className="mr-1 h-4 w-4 animate-spin" />
        ) : (
          <Mail className="mr-1 h-4 w-4" />
        )}
        {emailDraft.isPending ? t('email.generating') : t('email.generate')}
      </Button>

      {showDialog && emailDraft.data && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-lg rounded-lg bg-background shadow-lg">
            <EmailDraftDialog
              subject={emailDraft.data.subject}
              body={emailDraft.data.body}
              clientEmail={clientEmail}
              quoteStatus={quoteStatus}
              onRegenerate={handleRegenerate}
              isRegenerating={emailDraft.isPending}
              onStatusChange={onStatusChange}
              onClose={() => setShowDialog(false)}
              onSendEmail={handleSendEmail}
              isSending={sendEmail.isPending}
            />
          </div>
        </div>
      )}
    </>
  );
}
